enum FormSubElementTypes {
  TEXT_FIELD,
  NUMBER_FIELD,
  EMAIL_FIELD,
  PHONE_NUMBER_FIELD,
  DROP_DOWN_SELECT,  
  WORKY_CALENDAR,
  BOOLEAN_OPERATOR,
  TEXT_AREA,
  HASH_MAP,
  DYNAMIC_LIST,
  EMPTY_PLACEHOLDER
}

// Base interface with common properties
interface BaseFormContentElement {
  mandatory: boolean;
  elementHeaderMessage: string;
  elementPlaceHolderMessage: any;
  elementUniqueName: string;
  elementErrorMessage?: string;
  elementOnChangeHandler: (e: React.ChangeEvent<HTMLInputElement>) => void;
  elementIsDisabledHandler?: () => boolean;
  updateFieldValidity?: (fieldName:string, isValid:boolean) => void;
  elementCurrentValue: any;
  elementDefaultValue?: any;
}

// Text field specific interface
interface BooleanOperatorElement extends BaseFormContentElement {
    formSubElementTypes: FormSubElementTypes.BOOLEAN_OPERATOR;    
    placeholder?: string;
  }

// Text field specific interface
interface TextFieldElement extends BaseFormContentElement {
  formSubElementTypes: FormSubElementTypes.TEXT_FIELD;
  maxLength?: number;
  minLength?: number;
  placeholder?: string;
  customStyle?: React.CSSProperties; // Optional field for custom styles
}
// Number field specific interface
interface NumberFieldElement extends BaseFormContentElement {
  formSubElementTypes: FormSubElementTypes.NUMBER_FIELD;
  min?: number;
  max?: number;
  step?: number;
}

// Email field specific interface
interface EmailFieldElement extends BaseFormContentElement {
  formSubElementTypes: FormSubElementTypes.EMAIL_FIELD;  
  validationPattern?: RegExp;
}

// Phone number field specific interface
interface PhoneNumberFieldElement extends BaseFormContentElement {
  formSubElementTypes: FormSubElementTypes.PHONE_NUMBER_FIELD;
  format?: string;
  countryCode?: string;
}

// Dropdown single select specific interface
interface DropdownSelectElement extends BaseFormContentElement {
  formSubElementTypes: FormSubElementTypes.DROP_DOWN_SELECT;
  options: Array<{ value: string; label: string }>;
  maxSelections?: number;
}

// Calendar specific interface
interface WorkyCalendarElement extends BaseFormContentElement {
  formSubElementTypes: FormSubElementTypes.WORKY_CALENDAR;
  minDate?: Date;
  maxDate?: Date;
  disabledDates?: Date[];
  maxSelections?: number;
  /**
   * For re-occurring shift selection is true
   */
  isWeeklySelectionType: boolean;
}

// Text area specific interface
interface TextAreaElement extends BaseFormContentElement {
  formSubElementTypes: FormSubElementTypes.TEXT_AREA;
  maxLength?: number;
  minLength?: number;
  placeholder?: string;
  customStyle?: React.CSSProperties;
}

interface HashMapElement extends BaseFormContentElement {
  formSubElementTypes: FormSubElementTypes.HASH_MAP;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
  allowDuplicateKeys?: boolean;
  maxPairs?: number;
  keyValidation?: {
    pattern?: RegExp;
    minLength?: number;
    maxLength?: number;
  };
  valueValidation?: {
    pattern?: RegExp;
    minLength?: number;
    maxLength?: number;
  };
  elementCurrentValue: Record<string, string>; // JSON string of Array<{ key: string; value: string }>
}

// Dynamic list element interface for adding/removing custom objects
interface DynamicListElement extends BaseFormContentElement {
  formSubElementTypes: FormSubElementTypes.DYNAMIC_LIST;
  listItemFields: Array<{
    fieldName: string;
    fieldType: 'text' | 'number' | 'email' | 'phone' | 'dropdown' | 'textarea' | 'boolean';
    fieldLabel: string;
    fieldPlaceholder?: string;
    fieldValidation?: {
      pattern?: RegExp;
      minLength?: number;
      maxLength?: number;
      min?: number;
      max?: number;
      required?: boolean;
    };
    dropdownOptions?: Array<{ value: string; label: string }>;
  }>;
  maxItems?: number;
  minItems?: number;
  allowEmptyList?: boolean;
  addButtonText?: string;
  removeButtonText?: string;
  listItemTitle?: string;
  elementCurrentValue: Array<Record<string, any>>; // Array of objects with dynamic fields
  customStyle?: React.CSSProperties; // Custom styling for the entire dynamic list container
  listItemStyle?: React.CSSProperties; // Custom styling for individual list items
  fieldContainerStyle?: React.CSSProperties; // Custom styling for field containers
  addButtonStyle?: React.CSSProperties; // Custom styling for the add button
  removeButtonStyle?: React.CSSProperties; // Custom styling for remove buttons
  fieldLabelStyle?: React.CSSProperties; // Custom styling for field labels
  inputFieldStyle?: React.CSSProperties; // Custom styling for input fields
}

// Empty placeholder specific interface
interface EmptyPlaceholderElement extends BaseFormContentElement {
  formSubElementTypes: FormSubElementTypes.EMPTY_PLACEHOLDER;
  customStyle?: React.CSSProperties;
}

// Union type representing all possible form element types
type FormContentElement =
  | TextFieldElement
  | NumberFieldElement
  | EmailFieldElement
  | PhoneNumberFieldElement
  | DropdownSelectElement
  | WorkyCalendarElement
  | BooleanOperatorElement
  | TextAreaElement
  | HashMapElement
  | DynamicListElement
  | EmptyPlaceholderElement;

interface FormDefinitionStep {
  mandatory: boolean;
  formStepName: string;
  formStepContents: FormContentElement[];
}

interface FormDefinition {
  formName: string;
  formClassName?:string;
  formDefinitionSteps: FormDefinitionStep[];
//   submitButtonDefinition:SubmitButtonDefinition;  
    handleSubmitHandler?: () => void;
    handleCancelHandler?: () => void;
    submitButtonCustomStyle?: React.CSSProperties;
}

interface SubmitButtonDefinition {
    finalStepCount: number
    handleSubmitHandler?: () => void;
    handleCancelHandler?: () => void;
    currentStepValue?: number;
    nextStep?: () => void;
    prevStep?: () => void;
    customStyle?: React.CSSProperties;
}

export {
  FormSubElementTypes,
  BaseFormContentElement,
  TextFieldElement,
  NumberFieldElement,
  EmailFieldElement,
  PhoneNumberFieldElement,
  DropdownSelectElement,
  WorkyCalendarElement,
  FormContentElement,
  FormDefinitionStep,
  FormDefinition,
  SubmitButtonDefinition,
  BooleanOperatorElement,
  TextAreaElement,
  HashMapElement,
  DynamicListElement,
  EmptyPlaceholderElement
};
